"use client"

import Link from "next/link"
import Image from "next/image"
import { useState, useEffect } from "react"
import {
  Apple,
  Cpu,
  HardDrive,
  Zap,
  Shield,
  Download,
  ChevronRight,
  Check,
  Star,
  Terminal,
  Clock,
  Users,
} from "lucide-react"
import { Button } from "@/components/ui/button"

export default function HackintoshPage() {
  const [loaded, setLoaded] = useState(false)
  const [selectedVersion, setSelectedVersion] = useState("tahoe")

  useEffect(() => {
    setLoaded(true)
    window.scrollTo(0, 0)
  }, [])

  const macosVersions = [
    {
      id: "tahoe",
      name: "macOS Tahoe",
      version: "26",
      description: "Apple's latest macOS with cutting-edge features and breakthrough performance",
      size: "20.5 GB",
      popular: true,
      features: ["Next-Gen AI", "Enhanced Security", "Revolutionary UI", "Advanced Multitasking"],
      color: "from-teal-500/10 to-cyan-500/10",
      iconColor: "text-teal-400",
      slug: "macos-tahoe",
    },
    {
      id: "sequoia",
      name: "macOS Sequoia",
      version: "15",
      description: "Advanced macOS with AI features and improved performance",
      size: "19.2 GB",
      popular: true,
      features: ["Apple Intelligence", "iPhone Mirroring", "Window Tiling", "Passwords App"],
      color: "from-purple-500/10 to-pink-500/10",
      iconColor: "text-purple-400",
      slug: "macos-sequoia",
    },
    {
      id: "sonoma",
      name: "macOS Sonoma",
      version: "14",
      description: "Feature-rich macOS with enhanced productivity tools and gaming capabilities",
      size: "18.8 GB",
      features: ["Widgets on Desktop", "Game Mode", "Video Conferencing", "Safari Profiles"],
      color: "from-blue-500/10 to-cyan-500/10",
      iconColor: "text-blue-400",
      slug: "macos-sonoma",
    },
    {
      id: "ventura",
      name: "macOS Ventura",
      version: "13",
      description: "Powerful macOS with Stage Manager and improved continuity features",
      size: "18.2 GB",
      features: ["Stage Manager", "Continuity Camera", "Freeform", "iCloud Shared Photo Library"],
      color: "from-orange-500/10 to-red-500/10",
      iconColor: "text-orange-400",
      slug: "macos-ventura",
    },
    {
      id: "monterey",
      name: "macOS Monterey",
      version: "12",
      description: "Stable macOS release with Universal Control and SharePlay",
      size: "17.5 GB",
      features: ["Universal Control", "SharePlay", "Focus Modes", "Live Text"],
      color: "from-green-500/10 to-emerald-500/10",
      iconColor: "text-green-400",
      slug: "macos-monterey",
    },
  ]

  const benefits = [
    {
      icon: Zap,
      title: "Native Performance",
      description:
        "Experience near-native macOS performance on Proxmox VE with GPU passthrough support and optimized virtualization drivers.",
    },
    {
      icon: Shield,
      title: "Regularly Updated",
      description:
        "Our templates are updated regularly to support the latest macOS versions and security patches for maximum compatibility.",
    },
    {
      icon: Cpu,
      title: "Hardware Optimized",
      description:
        "Pre-configured with optimal CPU settings, memory allocation, and virtual hardware for smooth macOS operation.",
    },
    {
      icon: HardDrive,
      title: "Easy Deployment",
      description:
        "One-click deployment through our template script. No complex EFI configuration or boot loader setup required.",
    },
  ]

  const selectedMacOS = macosVersions.find((v) => v.id === selectedVersion) || macosVersions[0]

  return (
    <div className="flex min-h-screen flex-col bg-[#1a1c1e] text-white">
      <main className="flex-1">
        {/* Hero Section */}
        <section
          className={`relative w-full py-12 md:py-24 lg:py-32 bg-gradient-to-br from-[#1a1c1e] via-[#252729] to-[#1a1c1e] transition-all duration-1000 ${loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
        >
          {/* Animated background elements */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute -top-40 -right-40 w-80 h-80 bg-[#e57312]/5 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-500/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
          </div>

          <div className="container px-4 md:px-6 relative z-10">
            <div className="grid gap-6 lg:grid-cols-[1fr_500px] lg:gap-12 items-center">
              <div className="flex flex-col justify-center space-y-6">
                <div className="space-y-4">
                  <div className="inline-flex items-center rounded-full border border-[#e57312]/30 bg-[#e57312]/10 backdrop-blur-sm px-4 py-2 text-sm text-[#e57312]">
                    <Apple className="mr-2 h-4 w-4" />
                    <span className="font-medium">Hackintosh Templates for Proxmox</span>
                  </div>
                  <h1 className="text-4xl font-bold tracking-tighter sm:text-6xl xl:text-7xl/none">
                    Run <span className="text-[#e57312]">macOS</span> on Your Proxmox Server
                  </h1>
                  <p className="max-w-[600px] text-xl text-gray-300 leading-relaxed">
                    Deploy fully functional macOS virtual machines on Proxmox VE with our optimized Hackintosh
                    templates. From Monterey to Tahoe, experience the power of Apple's operating system on your
                    hardware.
                  </p>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-6 py-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-[#e57312]">5</div>
                    <div className="text-sm text-gray-400">macOS Versions</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-[#e57312]">1-Click</div>
                    <div className="text-sm text-gray-400">Deployment</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-[#e57312]">100%</div>
                    <div className="text-sm text-gray-400">Compatible</div>
                  </div>
                </div>

                <div className="flex flex-col gap-4 min-[400px]:flex-row">
                  <Button
                    size="lg"
                    asChild
                    className="group bg-gradient-to-r from-[#e57312] to-[#d4640f] hover:from-[#d4640f] hover:to-[#e57312] text-white shadow-lg hover:shadow-[#e57312]/25 transition-all duration-300"
                  >
                    <Link href="/templates/purchase">
                      Get Template Script
                      <Download className="ml-2 h-5 w-5" />
                    </Link>
                  </Button>

                </div>
              </div>

              {/* macOS Preview Card */}
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-[#e57312]/20 to-purple-500/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-500"></div>
                <div className="relative rounded-2xl border border-[#2c2e30]/50 bg-[#252729]/80 backdrop-blur-sm shadow-2xl overflow-hidden">
                  {/* macOS Window Header */}
                  <div className="bg-[#2c2e30]/80 backdrop-blur-sm h-10 flex items-center px-4">
                    <div className="flex space-x-2">
                      <div className="h-3 w-3 rounded-full bg-red-500"></div>
                      <div className="h-3 w-3 rounded-full bg-yellow-500"></div>
                      <div className="h-3 w-3 rounded-full bg-green-500"></div>
                    </div>
                    <div className="ml-4 text-sm text-gray-400 font-medium">macOS Tahoe</div>
                  </div>

                  {/* Terminal Preview */}
                  <div className="p-6">
                    <div className="bg-black/90 rounded-lg p-4 font-mono text-sm">
                      <div className="flex items-center text-green-400 mb-2">
                        <Terminal className="h-4 w-4 mr-2" />
                        <span>root@proxmox:~#</span>
                      </div>
                      <div className="text-gray-300 mb-4">
                        ./templateinstaller
                        <br />
                        <br />
                        <span className="text-[#e57312]">╔══════════════════════════════════════╗</span>
                        <br />
                        <span className="text-[#e57312]">║ HSVE Hackintosh Template Installer ║</span>
                        <br />
                        <span className="text-[#e57312]">╚══════════════════════════════════════╝</span>
                        <br />
                        <br />
                        <span className="text-gray-400">Select macOS Version:</span>
                        <br />
                        <br />
                        <span className="text-white">1. macOS Tahoe (26) - 20.5 GB</span>
                        <br />
                        <span className="text-white">2. macOS Sequoia (15) - 19.2 GB</span>
                        <br />
                        <span className="text-white">3. macOS Sonoma (14) - 18.8 GB</span>
                        <br />
                        <span className="text-white">4. macOS Ventura (13) - 18.2 GB</span>
                        <br />
                        <span className="text-white">5. macOS Monterey (12) - 17.5 GB</span>
                        <br />
                        <br />
                        <span className="text-gray-400">Enter selection [1-5]:</span>{" "}
                        <span className="animate-pulse">█</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="w-full py-16 md:py-24 bg-[#252729]">
          <div className="container px-4 md:px-6">
            <div className="flex flex-col items-center justify-center space-y-4 text-center mb-12">
              <div className="space-y-2">
                <div className="inline-block rounded-lg bg-[#1a1c1e] px-3 py-1 text-sm text-[#e57312]">
                  Why Choose Our Templates
                </div>
                <h2 className="text-3xl font-bold tracking-tighter md:text-4xl/tight">
                  Professional <span className="text-[#e57312]">Hackintosh</span> Solutions
                </h2>
                <p className="max-w-[900px] text-gray-400 md:text-xl/relaxed">
                  Pre-configured, optimized, and ready to deploy macOS templates for your Proxmox environment.
                </p>
              </div>
            </div>

            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              {benefits.map((benefit, index) => (
                <div
                  key={index}
                  className="bg-[#1a1c1e] rounded-xl border border-[#2c2e30] p-6 transition-all duration-300 hover:border-[#e57312]/50 hover:shadow-lg hover:shadow-[#e57312]/10 h-full flex flex-col"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-[#e57312]/10 text-[#e57312] mb-4">
                    <benefit.icon className="h-6 w-6" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">{benefit.title}</h3>
                  <p className="text-gray-400 flex-grow">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* macOS Versions Section */}
        <section className="w-full py-16 md:py-24 bg-[#1a1c1e]">
          <div className="container px-4 md:px-6">
            <div className="flex flex-col items-center justify-center space-y-4 text-center mb-12">
              <div className="space-y-2">
                <div className="inline-block rounded-lg bg-[#252729] px-3 py-1 text-sm text-[#e57312]">
                  Available Templates
                </div>
                <h2 className="text-3xl font-bold tracking-tighter md:text-4xl/tight">
                  Choose Your <span className="text-[#e57312]">macOS</span> Version
                </h2>
                <p className="max-w-[900px] text-gray-400 md:text-xl/relaxed">
                  From the latest Tahoe to stable Monterey, we have optimized templates for every macOS version.
                </p>
              </div>
            </div>

            {/* Version Selector */}
            <div className="flex flex-wrap justify-center gap-3 mb-8">
              {macosVersions.map((version) => (
                <button
                  key={version.id}
                  onClick={() => setSelectedVersion(version.id)}
                  className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                    selectedVersion === version.id
                      ? "bg-[#e57312] text-white shadow-lg shadow-[#e57312]/25"
                      : "bg-[#252729] text-gray-400 hover:bg-[#2c2e30] hover:text-white border border-[#2c2e30]"
                  }`}
                >
                  {version.name}
                  {version.popular && <Star className="inline h-4 w-4 ml-1 fill-current" />}
                  {version.experimental && <Zap className="inline h-4 w-4 ml-1" />}
                </button>
              ))}
            </div>

            {/* Selected Version Details */}
            <div className={`bg-gradient-to-br ${selectedMacOS.color} rounded-2xl border border-[#2c2e30] p-8`}>
              <div className="grid gap-8 lg:grid-cols-2 items-center">
                <div className="space-y-6">
                  <div className="flex items-center space-x-3">
                    <div
                      className={`flex h-16 w-16 items-center justify-center rounded-xl bg-[#1a1c1e] ${selectedMacOS.iconColor}`}
                    >
                      <Apple className="h-8 w-8" />
                    </div>
                    <div>
                      <h3 className="text-3xl font-bold text-white">{selectedMacOS.name}</h3>
                      <p className="text-gray-400">Version {selectedMacOS.version}</p>
                    </div>
                  </div>

                  <p className="text-lg text-gray-300">{selectedMacOS.description}</p>

                  <div className="flex items-center space-x-6">
                    <div>
                      <div className="text-sm text-gray-400">Download Size</div>
                      <div className="text-xl font-bold text-white">{selectedMacOS.size}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-400">Deploy Time</div>
                      <div className="text-xl font-bold text-white">~5 mins</div>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <Button
                      size="lg"
                      asChild
                      className="bg-[#e57312] hover:bg-[#d4640f] text-white"
                    >
                      <Link href={`/${selectedMacOS.slug}`}>
                        View Details
                        <ChevronRight className="ml-2 h-4 w-4" />
                      </Link>
                    </Button>
                    <Button
                      size="lg"
                      variant="outline"
                      asChild
                      className="border-[#e57312] text-[#e57312] hover:bg-[#e57312]/10 bg-transparent"
                    >
                      <Link href="/templates/purchase">
                        Get Template - £7.99
                      </Link>
                    </Button>
                  </div>
                </div>

                <div className="bg-[#1a1c1e]/50 backdrop-blur-sm rounded-xl p-6 border border-[#2c2e30]/50">
                  <h4 className="text-xl font-bold mb-4 text-white">Key Features</h4>
                  <ul className="space-y-3">
                    {selectedMacOS.features.map((feature, index) => (
                      <li key={index} className="flex items-center text-gray-300">
                        <Check className="h-5 w-5 text-[#e57312] mr-3 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="w-full py-16 md:py-24 bg-[#252729]">
          <div className="container px-4 md:px-6">
            <div className="flex flex-col items-center justify-center space-y-4 text-center mb-12">
              <div className="space-y-2">
                <div className="inline-block rounded-lg bg-[#1a1c1e] px-3 py-1 text-sm text-[#e57312]">
                  Simple Process
                </div>
                <h2 className="text-3xl font-bold tracking-tighter md:text-4xl/tight">
                  Deploy macOS in <span className="text-[#e57312]">3 Easy Steps</span>
                </h2>
              </div>
            </div>

            <div className="grid gap-6 md:grid-cols-3 max-w-5xl mx-auto">
              {[
                {
                  step: "1",
                  title: "Purchase & Download",
                  description: "Get the template script for £7.99 and download it to your Proxmox server.",
                  icon: Download,
                },
                {
                  step: "2",
                  title: "Select macOS Version",
                  description: "Run the script and choose your preferred macOS version from the menu.",
                  icon: Terminal,
                },
                {
                  step: "3",
                  title: "Deploy & Enjoy",
                  description: "The script automatically downloads and configures your macOS VM. Ready in minutes!",
                  icon: Clock,
                },
              ].map((item, index) => (
                <div
                  key={index}
                  className="bg-[#1a1c1e] rounded-xl border border-[#2c2e30] p-6 relative"
                >
                  <div className="absolute -top-4 -left-4 h-12 w-12 rounded-full bg-[#e57312] flex items-center justify-center text-white font-bold text-xl shadow-lg">
                    {item.step}
                  </div>
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-[#e57312]/10 text-[#e57312] mb-4 ml-8">
                    <item.icon className="h-6 w-6" />
                  </div>
                  <h3 className="text-xl font-bold mb-2 text-white">{item.title}</h3>
                  <p className="text-gray-400">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="w-full py-16 md:py-24 bg-gradient-to-br from-[#1a1c1e] via-[#252729] to-[#1a1c1e]">
          <div className="container px-4 md:px-6">
            <div className="flex flex-col items-center justify-center space-y-8 text-center">
              <div className="space-y-4 max-w-3xl">
                <h2 className="text-4xl font-bold tracking-tighter md:text-5xl">
                  Ready to Run <span className="text-[#e57312]">macOS</span> on Proxmox?
                </h2>
                <p className="text-xl text-gray-300">
                  Join hundreds of users who have successfully deployed macOS VMs with our optimized Hackintosh
                  templates.
                </p>
              </div>

              <div className="flex flex-col gap-4 min-[400px]:flex-row">
                <Button
                  size="lg"
                  asChild
                  className="bg-gradient-to-r from-[#e57312] to-[#d4640f] hover:from-[#d4640f] hover:to-[#e57312] text-white shadow-lg hover:shadow-[#e57312]/25 px-8"
                >
                  <Link href="/templates/purchase">
                    Get Started for £7.99
                    <ChevronRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  asChild
                  className="border-[#e57312]/50 text-[#e57312] hover:bg-[#e57312]/10 bg-transparent"
                >
                  <Link href="/contact">
                    Contact Us
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  )
}
